"use client";
import React, { useState } from 'react';
import { 
  Upload,
  BriefcaseIcon,
  Users,
  GraduationCap,
  Clock,
  CheckCircle,
  ArrowRight
} from 'lucide-react';


const CareerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    message: ''
  });

  const openPositions = [
    {
      title: "Senior VLSI Design Engineer",
      type: "Full Time",
      location: "Remote/Onsite",
      experience: "5+ years",
      requirements: [
        "Experience with RTL design and verification",
        "Proficiency in SystemVerilog/UVM",
        "Knowledge of digital design flows",
        "Experience with synthesis and timing analysis"
      ]
    },
    {
      title: "FPGA Developer",
      type: "Full Time",
      location: "Remote/Onsite",
      experience: "3+ years",
      requirements: [
        "Experience with FPGA design and implementation",
        "Proficiency in Verilog/VHDL",
        "Knowledge of Xilinx/Intel FPGA tools",
        "Experience with high-speed interfaces"
      ]
    },
    {
      title: "Embedded Systems Engineer",
      type: "Full Time",
      location: "Remote/Onsite",
      experience: "4+ years",
      requirements: [
        "Strong C/C++ programming skills",
        "Experience with microcontroller architectures",
        "Knowledge of RTOS concepts",
        "PCB design experience is a plus"
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Implement form submission logic here
    console.log(formData);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, resume: file }));
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Join Our Team of
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                {" "}Innovation Leaders
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Build the future of semiconductor technology with us. We're looking for talented 
              individuals who are passionate about innovation and excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Join iVLSI Technologies?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-gray-50 rounded-xl">
              <GraduationCap className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Continuous Learning</h3>
              <p className="text-gray-600">
                Access to cutting-edge technology and regular training opportunities to enhance your skills.
              </p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl">
              <Users className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Collaborative Environment</h3>
              <p className="text-gray-600">
                Work with industry experts and be part of an innovative team solving complex challenges.
              </p>
            </div>
            <div className="p-6 bg-gray-50 rounded-xl">
              <Clock className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-semibold mb-4">Work-Life Balance</h3>
              <p className="text-gray-600">
                Flexible work arrangements and policies that support your personal and professional growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Open Positions</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {openPositions.map((position, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{position.title}</h3>
                <div className="flex items-center text-gray-600 mb-4">
                  <BriefcaseIcon className="w-4 h-4 mr-2" />
                  <span className="mr-4">{position.type}</span>
                  <span>{position.location}</span>
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  Experience: {position.experience}
                </div>
                <div className="space-y-2 mb-6">
                  {position.requirements.map((req, i) => (
                    <div key={i} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{req}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' })} 
                        className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center">
                  Apply Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Apply Now</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                  Position Applied For
                </label>
                <select
                  id="position"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.position}
                  onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                >
                  <option value="">Select Position</option>
                  {openPositions.map((pos, index) => (
                    <option key={index} value={pos.title}>{pos.title}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Years of Experience
                </label>
                <input
                  type="text"
                  id="experience"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.experience}
                  onChange={(e) => setFormData(prev => ({ ...prev, experience: e.target.value }))}
                />
              </div>

              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Resume
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="resume-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                        <span>Upload a file</span>
                        <input 
                          id="resume-upload" 
                          type="file" 
                          className="sr-only" 
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                />
              </div>

              <button
                type="submit"
                className="w-full px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold flex items-center justify-center"
              >
                Submit Application
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerPage;